import { deriveGraph, type GraphTask } from '@agavi/blocks';

import type { Task } from '@/types/experience';

/**
 * The experience split the way a teacher reads it: the main sequence, then each named pathway.
 *
 * ## Why this is shared
 *
 * Two lists show it — Flow's "go to" picker and the editor's jump menu — and they must not
 * disagree about which tasks are on the spine or which pathway a task belongs to. Flow built it
 * inline first; this is that code lifted out rather than rewritten, so the pickers cannot drift.
 *
 * ## Grouping is by `pathwayId`, which is NOT how sequencing works
 *
 * Deliberate, and the distinction matters. `taskTrack` walks `deriveGraph.nextOf`, whose lanes
 * are keyed on `pathwayOf` — the task a detour hangs off — because that is what decides where
 * NEXT goes. A teacher scanning a list wants the named thing instead: "Remediation", not "the
 * tasks hanging off task 3". So the arrows and this list answer different questions and key on
 * different fields on purpose.
 *
 * The spine still comes from `deriveGraph`, so a detour orphaned by a deleted host appears there
 * rather than vanishing from the menu entirely.
 */
export interface TaskPathwayGroup {
  pathwayId: string;
  /** The teacher's name for it, when they gave one. */
  name?: string;
  members: Task[];
}

export interface TaskGroups {
  /** Main-sequence tasks in order, each with its 1-based position for display. */
  spine: { task: Task; num: number }[];
  /** Named pathways, in the order their first member appears. */
  pathways: TaskPathwayGroup[];
}

export function taskGroups(tasks: Task[]): TaskGroups {
  const byId = new Map(tasks.map(t => [t.id, t]));
  const graph = deriveGraph(tasks as unknown as GraphTask[]);

  const spine = graph.spine
    .map((id, i) => ({ task: byId.get(id), num: i + 1 }))
    .filter((x): x is { task: Task; num: number } => !!x.task);

  const order: string[] = [];
  const groups = new Map<string, TaskPathwayGroup>();
  for (const task of tasks) {
    if (!task.pathwayId) continue;
    let group = groups.get(task.pathwayId);
    if (!group) {
      group = { pathwayId: task.pathwayId, name: task.pathwayName || undefined, members: [] };
      groups.set(task.pathwayId, group);
      order.push(task.pathwayId);
    }
    group.members.push(task);
  }

  return {
    spine,
    pathways: order.map(id => groups.get(id)!).filter(g => g.members.length > 0),
  };
}
